/* eslint-disable react/prefer-stateless-function */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon, Divider } from 'semantic-ui-react';
import { colors } from 'utils/colors';
import DistributionChart, {
  parseBuckets,
  bucketLabel,
  minus,
} from './DistributionChart';

const RED = colors.red;
const BLUE = colors.blue;
const NAVY = colors.navyBlue;

const ScenarioTable = ({ title, color, note, rows, total }) => (
  <TableCard>
    <TableTitle color={color}>{title}</TableTitle>
    <TableNote>{note}</TableNote>
    <table>
      <thead>
        <tr>
          <th>Years</th>
          <th>%</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => (
          <tr key={b.key}>
            <td>{bucketLabel(b)}</td>
            <td>{b.value.toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td>{Number(total).toFixed(2)}</td>
        </tr>
      </tfoot>
    </table>
  </TableCard>
);

ScenarioTable.propTypes = {
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  note: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

class Result extends Component {
  render() {
    const { result, isFetching, pdf } = this.props;

    if (!result) return null;

    const dist = result.distribution_actual;
    const buckets = parseBuckets(dist.hist);
    const safeRows = buckets.filter((b) => !b.isRuin);
    // closest to zero first, "Less than -30" last
    const ruinRows = buckets.filter((b) => b.isRuin).reverse();

    const ruinPercent = !result.ruinProbability
      ? 0
      : Math.round(result.ruinProbability * 100);

    const sumRuin = Number(dist.sum_ruin);
    const sumSafe = Number(dist.sum_safe);
    const ruinShare =
      sumRuin + sumSafe > 0 ? (sumRuin / (sumRuin + sumSafe)) * 100 : 0;

    const hasMedian = dist.median !== null && dist.median !== undefined;
    const medianAbs = hasMedian ? Math.abs(Number(dist.median)) : null;

    return (
      <div id="longevity-ref" style={{ opacity: isFetching ? 0.3 : 1 }}>
        <Divider
          style={{
            margin: '2rem auto',
            width: '80%',
            maxWidth: '800px',
            color: '#ececf0',
          }}
        />
        <Wrapper>
          <Heading>Results</Heading>

          <Panel>
            <SummaryTop>
              <div>
                <SummaryText>
                  The probability that you will outlive your money is:
                </SummaryText>
                <Info>Based on 10,000 simulations</Info>
              </div>
              <BigPercent>{ruinPercent}%</BigPercent>
            </SummaryTop>

            <SplitBar>
              <div style={{ width: `${ruinShare}%`, background: RED }} />
              <div style={{ flex: 1, background: BLUE }} />
            </SplitBar>

            <SplitLegend>
              <LegendLabel color={RED}>
                Ruin scenarios:{' '}
                <b style={{ marginLeft: '0.25rem' }}>{sumRuin.toFixed(2)}%</b>
              </LegendLabel>
              <LegendLabel color={BLUE}>
                Safe scenarios:{' '}
                <b style={{ marginLeft: '0.25rem' }}>{sumSafe.toFixed(2)}%</b>
              </LegendLabel>
            </SplitLegend>

            {hasMedian && (
              <MedianRow>
                <div>
                  <SummaryText>Median years for ruin scenarios:</SummaryText>
                  <Info>
                    In the median (middle) scenario, your money runs out about{' '}
                    {medianAbs} years before you do.
                  </Info>
                </div>
                <MedianValue>{minus(`-${medianAbs}`)} years</MedianValue>
              </MedianRow>
            )}
          </Panel>

          <Panel>
            <DistributionChart data={dist} />
          </Panel>

          {pdf && <div style={{ pageBreakAfter: 'always' }} />}

          <TablesGrid>
            <ScenarioTable
              title="Safe Scenarios"
              color={BLUE}
              note="Probability that your money will outlive you by:"
              rows={safeRows}
              total={dist.sum_safe}
            />
            <ScenarioTable
              title="Ruin Scenarios"
              color={RED}
              note="Probability that you will outlive your money by:"
              rows={ruinRows}
              total={dist.sum_ruin}
            />
          </TablesGrid>

          {result.ruinProbability > 0.05 ? (
            <Banner>
              <BannerIcon>
                <Icon name="info" />
              </BannerIcon>
              <div>
                As a comparison, if you want to target a small probability of
                ruin, say 5%, you should withdraw no more than{' '}
                <b>${result.maxSpendingNoRuin.toLocaleString()}</b> per year.
              </div>
            </Banner>
          ) : (
            <Banner>
              <BannerIcon>
                <Icon name="thumbs up" />
              </BannerIcon>
              <div>
                Good news. The chance of your outliving your money falls below
                the 5% safe target, commonly used in the retirement literature.
              </div>
            </Banner>
          )}
        </Wrapper>
      </div>
    );
  }
}

Result.propTypes = {
  result: PropTypes.object,
  isFetching: PropTypes.bool,
  pdf: PropTypes.bool,
};

Result.defaultProps = {
  result: null,
  isFetching: false,
  pdf: false,
};

export default Result;

const Wrapper = styled.div`
  width: 80%;
  max-width: 800px;
  margin: 0 auto;
  padding-bottom: 1.5rem;
`;

const Heading = styled.h3`
  margin: 1.5rem 0 1rem 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2b2b2b;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #e6e6ec;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
`;

const SummaryTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SummaryText = styled.div`
  font-size: 18px;
  color: #333;
`;

const Info = styled.div`
  margin-top: 0.25rem;
  font-size: 15px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.55);
`;

const BigPercent = styled.div`
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1;
  color: #c62828;
`;

const SplitBar = styled.div`
  display: flex;
  height: 14px;
  border-radius: 4px;
  overflow: hidden;
`;

const SplitLegend = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.9rem 0 1rem 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eeeeee;
`;

const LegendLabel = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  color: #333;

  &:before {
    content: '';
    width: 0.7rem;
    height: 0.7rem;
    margin-right: 0.45rem;
    border-radius: 2px;
    background: ${(p) => p.color};
  }
`;

const MedianRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const MedianValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  white-space: nowrap;
  color: ${NAVY};
`;

const TablesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const TableCard = styled.div`
  background: #fff;
  border: 1px solid #e6e6ec;
  border-radius: 10px;
  overflow: hidden;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 16px;
    color: #333;
  }

  th,
  td {
    padding: 0.7rem 1rem;
    text-align: left;
  }

  th + th,
  td + td {
    text-align: right;
  }

  thead th {
    font-weight: 700;
    border-top: 1px solid #e6e6ec;
    border-bottom: 1px solid #e6e6ec;
    background: #f7f7f9;
  }

  tbody td {
    border-bottom: 1px solid #f0f0f3;
  }

  tfoot td {
    font-weight: 700;
    background: #f7f7f9;
  }
`;

const TableTitle = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1rem 0.25rem 1rem;
  font-weight: 700;
  font-size: 16px;
  color: #222;

  &:before {
    content: '';
    width: 0.7rem;
    height: 0.7rem;
    margin-right: 0.5rem;
    border-radius: 2px;
    background: ${(p) => p.color};
  }
`;

const TableNote = styled.div`
  padding: 0 1rem 0.9rem 1rem;
  font-size: 14px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.55);
`;

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1.1rem 1.4rem;
  border-radius: 8px;
  border: 1px solid #d9deed;
  background: #eff1f9;
  font-size: 16px;
  line-height: 1.5;
  color: #2f3b52;

  b {
    color: #1f2a3d;
  }
`;

const BannerIcon = styled.div`
  flex: 0 0 auto;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #475b82;
  color: #fff;

  i.icon {
    margin: 0;
    font-size: 0.8rem;
    line-height: 1;
  }
`;
